import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface XpBreakdownItem {
  activity_type: string;
  total_xp: number;
  percentage: number;
}

export const useXpBreakdown = (userId?: string) => {
  return useQuery({
    queryKey: ['xp-breakdown', userId],
    queryFn: async () => {
      if (!userId) return null;

      // Fetch all XP transactions for the user
      const { data: transactions, error } = await supabase
        .from('xp_transactions')
        .select('activity_type, amount')
        .eq('user_id', userId);

      if (error) throw error;

      // Aggregate by activity_type
      const breakdown = new Map<string, number>();
      let totalXp = 0;

      transactions?.forEach((txn) => {
        const current = breakdown.get(txn.activity_type) || 0;
        breakdown.set(txn.activity_type, current + txn.amount);
        totalXp += txn.amount;
      });

      // Convert to array and calculate percentages
      const items: XpBreakdownItem[] = Array.from(breakdown.entries())
        .map(([activity_type, total_xp]) => ({
          activity_type,
          total_xp,
          percentage: totalXp > 0 ? Math.round((total_xp / totalXp) * 100) : 0,
        }))
        .sort((a, b) => b.total_xp - a.total_xp);

      return {
        items,
        totalXp,
      };
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Activity type labels with icons
export const activityTypeLabels: Record<string, { label: string; icon: string }> = {
  book_finished: { label: 'Finished Books', icon: '📚' },
  rating_created: { label: 'Book Ratings', icon: '⭐' },
  focus_session_completed: { label: 'Focus Sessions', icon: '🔥' },
  review_created: { label: 'Reviews', icon: '✍️' },
  reading_progress_updated: { label: 'Reading Progress', icon: '📖' },
  book_started: { label: 'Books Started', icon: '🎯' },
  book_saved: { label: 'Saved Books', icon: '💾' },
  review_liked: { label: 'Review Likes', icon: '❤️' },
  review_commented: { label: 'Comments', icon: '💬' },
  achievement_unlocked: { label: 'Achievements', icon: '🏆' },
  daily_quest_completed: { label: 'Daily Quests', icon: '✨' },
  focus_milestone: { label: 'Focus Milestones', icon: '🎖️' },
  level_up: { label: 'Level Ups', icon: '⬆️' },
};

export const getActivityLabel = (activityType: string): { label: string; icon: string } => {
  return activityTypeLabels[activityType] || { label: activityType.replace(/_/g, ' '), icon: '📊' };
};
